import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from 'styled-components';
import { useIntl, FormattedMessage } from 'react-intl';
import { useDebouncedCallback } from 'use-debounce';
import { point } from '@turf/helpers';
import bearing from '@turf/bearing';
import distance from '@turf/distance';
import { DUMMY_LAT, DUMMY_LNG } from '#/map/constants';
import { ScreenReaderText } from '#/shared/components/Accessibility';
import * as StopSign from '#/transit-tracker/components/StopSign';
import RouteBadge from '#/shared/components/RouteBadge';
import {
  createSignKey,
  getStopFromArrival,
} from '#/transit-tracker/utils/arrivals';
import StopListSkeletons from '#/shared/components/Skeletons/StopListSkeletons';
import Button from '#/shared/components/Buttons';
import { PlannerIcon } from '#/transit-tracker/components/CurrentLocationPrompt';
import withinServiceArea from '#/transit-tracker/utils/withinServiceArea';
import ArrivalTimeBadge from '#/transit-tracker/components/ArrivalTimeBadge';
import FavoriteButton from '#/transit-tracker/components/FavoriteButton';
import {
  ROUTE_COLOR_MAPPING,
  SKELETON_INTERVAL,
} from '#/transit-tracker/constants';
import useCurrentLocation from '#/shared/hooks/useCurrentLocation';
import * as Styled from './styled';

function StopsList({
  arrivals,
  toggleFavoriteStop,
  isFavoriteStop,
  navigateToStopAndSign,
  navigateToPlanner,
  highlightStop,
  dataGtm,
  componentMode,
  selectedLocation,
}) {
  const theme = useTheme();
  const { currentLocation } = useCurrentLocation();
  const { lat: currentLocationLat = 0, lon: currentLocationLon = 0 } =
    currentLocation || {};

  const [skeletonMode, setSkeletonMode] = useState(true);

  useEffect(() => {
    if (arrivals.length >= 0) {
      setSkeletonMode(false);
    } else {
      setSkeletonMode(true);
    }
    return () => setSkeletonMode(true);
  }, [arrivals.length, componentMode]);

  const debouncedHS = useDebouncedCallback(a => {
    if (a != null) {
      highlightStop(
        getStopFromArrival({
          ...a,
          ...{ isFavorite: isFavoriteStop(a) },
        })
      );
    } else {
      highlightStop(null);
    }
  }, 300);

  /** TODO: Remove legacy check
   * https://trimet.atlassian.net/browse/PRJ77-569
   */

  const legacyCheck = theme.name === 'Legacy';

  const noResultContent = () => {
    return (
      <>
        <StopSign.StopsAlternativeMessage>
          <FormattedMessage id="nearbystops.nostops" />
        </StopSign.StopsAlternativeMessage>
        <Styled.ButtonWrap>
          <Button
            data-testid="no-arrivals-button"
            variant={legacyCheck ? 'outline-reverse' : 'outline'}
            onClick={navigateToPlanner}
          >
            <PlannerIcon $userHasFavoriteStops />
            <FormattedMessage id="nearbystops.plannerbutton" />
          </Button>
        </Styled.ButtonWrap>
      </>
    );
  };

  const skeletonContent = modeString => {
    if (modeString === 'favorites') {
      return <StopListSkeletons />;
    }
    return (
      <>
        <StopListSkeletons />
        <StopListSkeletons />
      </>
    );
  };

  const intl = useIntl();

  function getDirectionData(stopLat, stopLng, modeString) {
    let stopLatForUse = stopLat;
    let stopLngForUse = stopLng;

    let starterLon;
    let starterLat;

    if (process.env.JEST_WORKER_ID !== undefined) {
      starterLon = DUMMY_LNG;
      starterLat = DUMMY_LAT;
      stopLatForUse = DUMMY_LAT;
      stopLngForUse = DUMMY_LNG;
    } else if (modeString === 'nearbyUser' || modeString === 'favorites') {
      starterLon = currentLocationLon;
      starterLat = Number(currentLocationLat);
    } else {
      starterLon = selectedLocation?.lon;
      starterLat = Number(selectedLocation?.lat);
    }

    if (!starterLon || !starterLat || !stopLngForUse || !stopLatForUse) {
      return {};
    }
    const start = point([Number(starterLon), Number(starterLat)]);
    const end = point([Number(stopLngForUse), Number(stopLatForUse)]);
    const originalDist = distance(start, end, { units: 'feet' });
    let dist = originalDist;
    let unitIntlId = 'unit-feet';

    // Convert to miles for distances greater than 2500 feet, and adjust
    // the unit and value accordingly.
    if (dist > 2500) {
      dist /= 5280; // Convert feet to miles
      dist = Math.round(dist * 10) / 10; // Round to one decimal place

      if (dist < 1) {
        dist = dist.toString().startsWith('0')
          ? dist.toString().substring(1)
          : dist.toString();
      }

      unitIntlId = dist === 1 ? 'unit-mile' : 'unit-miles';
    } else {
      dist = Math.ceil(dist); // Round up to the nearest foot for shorter distances
      // Adjust the unit for 1 foot
      if (dist === 1) {
        unitIntlId = 'unit-foot';
      }
    }

    let directionIntlId = 'direction-unknown';
    const angle = bearing(start, end);
    const normalizedAngle = (angle + 360) % 360;
    if (normalizedAngle >= 337.5 || normalizedAngle < 22.5) {
      directionIntlId = 'direction-north';
    } else if (normalizedAngle >= 22.5 && normalizedAngle < 67.5) {
      directionIntlId = 'direction-northeast';
    } else if (normalizedAngle >= 67.5 && normalizedAngle < 112.5) {
      directionIntlId = 'direction-east';
    } else if (normalizedAngle >= 112.5 && normalizedAngle < 157.5) {
      directionIntlId = 'direction-southeast';
    } else if (normalizedAngle >= 157.5 && normalizedAngle < 202.5) {
      directionIntlId = 'direction-south';
    } else if (normalizedAngle >= 202.5 && normalizedAngle < 247.5) {
      directionIntlId = 'direction-southwest';
    } else if (normalizedAngle >= 247.5 && normalizedAngle < 292.5) {
      directionIntlId = 'direction-west';
    } else if (normalizedAngle >= 292.5 && normalizedAngle < 337.5) {
      directionIntlId = 'direction-northwest';
    }

    return {
      direction: intl.formatMessage({ id: `nearbystops.${directionIntlId}` }),
      dist,
      originalDist,
      unit: intl.formatMessage({ id: `nearbystops.${unitIntlId}` }),
    };
  }

  const preprocessArrivals = () => {
    const arrivalsWithDistance = arrivals.map(arrival => {
      const directionData = getDirectionData(
        arrival.stopLat,
        arrival.stopLng,
        componentMode
      );
      return {
        ...arrival,
        originalDist: directionData.originalDist,
        distance: directionData.dist,
        isFavorite: isFavoriteStop(arrival),
      };
    });

    const sortedArrivals = arrivalsWithDistance.sort(
      (a, b) => a.originalDist - b.originalDist
    );

    const arrivalsGroupedByRouteAndStop = sortedArrivals.reduce(
      (acc, arrival) => {
        const key = `${arrival.stopId}-${arrival.route.id}`;
        return {
          ...acc,
          [key]: acc[key] ? acc[key] : arrival,
        };
      },
      {}
    );

    return Object.values(arrivalsGroupedByRouteAndStop);
  };

  const doNotShowDistanceLabel =
    currentLocationLat === 0 && currentLocationLon === 0;

  function adjustStopName(stopName, arrivalsForStop) {
    const routeTypes = new Set(
      arrivalsForStop.map(arrival => arrival.route.routeSubType)
    );

    if (routeTypes.size === 2) {
      if (routeTypes.has('Bus') && routeTypes.has('BRT')) {
        return `${stopName} Bus Stop`;
      }

      if (routeTypes.has('Bus') && routeTypes.has('Streetcar')) {
        return `${stopName} Streetcar/Bus Stop`;
      }
    }

    if (routeTypes.size === 1) {
      const type = routeTypes.values().next().value;
      switch (type) {
        case 'BRT':
          return `${stopName} Bus Stop`;

        case 'Bus':
          return `${stopName} Bus Stop`;
        case 'Light Rail':
          return `${stopName}`;
        case 'Streetcar':
          return `${stopName} Streetcar Stop`;
        default:
          return stopName;
      }
    }

    return stopName;
  }

  const stopsContent = modeString => {
    const preprocessedArrivals = preprocessArrivals();
    const groupedArrivals = preprocessedArrivals.reduce(
      (accumulator, arrival) => {
        const newAcc = { ...accumulator };
        newAcc[arrival.stopName] = newAcc[arrival.stopName] || [];
        newAcc[arrival.stopName].push(arrival);
        return newAcc;
      },
      {}
    );

    return Object.entries(groupedArrivals).map(
      ([stopName, arrivalsForStop]) => {
        const adjustedStopName = adjustStopName(stopName, arrivalsForStop);

        const sortedArrivalsForStop = arrivalsForStop.sort((a, b) => {
          const orderA = ROUTE_COLOR_MAPPING[a.route.id];
          const orderB = ROUTE_COLOR_MAPPING[b.route.id];

          if (orderA && orderB) {
            if (orderA !== orderB) {
              return orderA - orderB;
            }
            return a.vanityName.localeCompare(b.vanityName);
          }

          if (orderA) return -1;
          if (orderB) return 1;

          if (a.route.id !== b.route.id) {
            return a.route.id - b.route.id;
          }

          return a.vanityName.localeCompare(b.vanityName);
        });

        const primeGroupArrival = sortedArrivalsForStop[0];
        const directionData = getDirectionData(
          primeGroupArrival?.stopLat,
          primeGroupArrival?.stopLng,
          modeString
        );

        return (
          <React.Fragment key={primeGroupArrival.id}>
            <Styled.StationInfoLane>
              <Styled.GroupedStopName>
                {adjustedStopName}
              </Styled.GroupedStopName>
              {!doNotShowDistanceLabel && (
                <Styled.GroupedStopDistance>
                  {directionData.dist} {directionData.unit}{' '}
                  {directionData.direction}{' '}
                </Styled.GroupedStopDistance>
              )}
            </Styled.StationInfoLane>
            {sortedArrivalsForStop.map(a => (
              <StopSign.StopRow key={a.id}>
                <StopSign.StopNavigationButton
                  onClick={() =>
                    navigateToStopAndSign(a.stopId, [createSignKey(a.fullSign)])
                  }
                  onFocus={() => debouncedHS(a)}
                  onMouseEnter={() => {
                    debouncedHS(a);
                  }}
                  onMouseLeave={() => {
                    debouncedHS(null);
                  }}
                  onBlur={() => debouncedHS(null)}
                >
                  <StopSign.RouteColumn>
                    <RouteBadge routeId={a.route.id} />
                  </StopSign.RouteColumn>
                  <StopSign.ArrivalDescriptionColumn>
                    <StopSign.VanityNameDiv>
                      <FormattedMessage
                        id="nearbystops.toname"
                        values={{ name: a.vanityName }}
                      />
                    </StopSign.VanityNameDiv>
                  </StopSign.ArrivalDescriptionColumn>
                  <StopSign.ArrivalTimeColumn>
                    <ArrivalTimeBadge arrival={a} />
                  </StopSign.ArrivalTimeColumn>
                  <ScreenReaderText>
                    <FormattedMessage id="nearbystops.stopdetails_acc" />
                  </ScreenReaderText>
                </StopSign.StopNavigationButton>{' '}
                <FavoriteButton
                  isFavorite={isFavoriteStop(a)}
                  toggleFavorite={() => toggleFavoriteStop(a)}
                  type="stop"
                />
              </StopSign.StopRow>
            ))}
          </React.Fragment>
        );
      }
    );
  };

  const [minSkeletonDurationPassed, setMinSkeletonDurationPassed] =
    useState(false);

  useEffect(() => {
    const timerId = setTimeout(
      () => setMinSkeletonDurationPassed(true),
      SKELETON_INTERVAL
    );

    return () => clearTimeout(timerId);
  }, [setMinSkeletonDurationPassed]);

  const stopListBodyContents = modeString => {
    if (modeString === 'favorites') {
      return arrivals.length > 0
        ? stopsContent(modeString)
        : skeletonContent(modeString);
    }

    if (selectedLocation) {
      const { lat, lon, state } = selectedLocation;
      const inService = withinServiceArea(lat, lon, state);

      if (
        !inService &&
        (modeString === 'nearbyUser' || modeString === 'nearbyLocation')
      ) {
        return noResultContent();
      }
    }

    if (arrivals.length > 0) {
      return stopsContent(modeString);
    }

    if (!skeletonMode && minSkeletonDurationPassed) {
      return noResultContent();
    }
    return skeletonContent(modeString);
  };

  return (
    <Styled.StopListBody dataGtm={dataGtm}>
      {stopListBodyContents(componentMode)}
    </Styled.StopListBody>
  );
}

StopsList.propTypes = {
  /** A collection of arrivals to be displayed */
  arrivals: PropTypes.arrayOf(PropTypes.shape({}).isRequired),
  /**
   * Takes the combination of a stop and line ID and adds/removes it from a
   * favorite stops collection
   */
  toggleFavoriteStop: PropTypes.func.isRequired,
  /** Checks if a given stop and line are favorited */
  isFavoriteStop: PropTypes.func.isRequired,
  /** Navigate to stop detail page with supplied signs opened */
  navigateToStopAndSign: PropTypes.func.isRequired,
  /** Navigate to the Trip Planner trip tool */
  navigateToPlanner: PropTypes.func.isRequired,
  /** Propagates the stop so it becomes highlighted the map view */
  highlightStop: PropTypes.func.isRequired,
  /** Optional data attribute for google tag manager */
  dataGtm: PropTypes.string,
  /** On Location view, get the coordinates to detect if it changes */
  selectedLocation: PropTypes.shape({
    lat: PropTypes.number,
    lon: PropTypes.number,
    name: PropTypes.string,
    state: PropTypes.string,
  }),
  /**
   * Indicates the type of arrivals that are being supplied which affects the
   * presentation of the component
   */
  componentMode: PropTypes.oneOf(['favorites', 'nearbyLocation', 'nearbyUser'])
    .isRequired,
};

StopsList.defaultProps = {
  arrivals: [],
  selectedLocation: null,
  dataGtm: 'stops',
};

export default StopsList;
